import { useEffect, useState } from "react";
import Seo from "@/components/base/Seo";
import AdminPageHeader from "../components/AdminPageHeader";
import AdminStatCard from "../components/AdminStatCard";
import { AdminCategoryChart, AdminTrafficChart } from "../components/AdminCharts";
import { useAuth } from "@/context/AuthContext";
import { getAdminCategoryStats, getAdminTrend } from "@/services/adminApi";
import type { AdminCategoryStat, AdminTrendPoint } from "@/types";

export default function AdminAnalytics() {
  const { token } = useAuth();
  const [trend, setTrend] = useState<AdminTrendPoint[]>([]);
  const [categoryStats, setCategoryStats] = useState<AdminCategoryStat[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(false);
    Promise.all([getAdminTrend(token), getAdminCategoryStats(token)])
      .then(([t, c]) => {
        if (!active) return;
        setTrend(t);
        setCategoryStats(c);
      })
      .catch(() => active && setError(true))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [token]);

  if (loading) {
    return <div className="h-96 animate-pulse rounded-lg border border-background-200 bg-background-100/60"></div>;
  }

  if (error || trend.length === 0) {
    return (
      <div className="rounded-lg border border-secondary-200 bg-secondary-50 p-6 text-center">
        <i className="ri-error-warning-line text-2xl text-secondary-600"></i>
        <p className="mt-2 text-sm text-secondary-900">Analytics could not be loaded. Please refresh.</p>
      </div>
    );
  }

  const latest = trend[trend.length - 1];
  const previous = trend[trend.length - 2] ?? latest;
  const totalViews = trend.reduce((s, p) => s + p.views, 0);
  const avgViews = Math.round(totalViews / trend.length);
  const viewsGrowth = previous.views ? (((latest.views - previous.views) / previous.views) * 100) : 0;
  const revenueGrowth = previous.revenue ? (((latest.revenue - previous.revenue) / previous.revenue) * 100) : 0;
  const maxCategory = Math.max(...categoryStats.map((c) => c.count), 1);

  return (
    <>
      <Seo title="Analytics" path="/admin/analytics" />

      <AdminPageHeader
        icon="ri-line-chart-line"
        title="Analytics"
        subtitle="Traffic, growth and category performance across the whole directory."
      />

      <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <AdminStatCard label="Total views (10 mo)" value={totalViews.toLocaleString()} icon="ri-eye-line" tone="primary" />
        <AdminStatCard label="Avg. monthly views" value={avgViews.toLocaleString()} icon="ri-bar-chart-2-line" change={Number(viewsGrowth.toFixed(1))} hint="MoM" tone="neutral" />
        <AdminStatCard label="Live listings" value={latest.listings.toLocaleString()} icon="ri-store-2-line" change={4.8} hint="vs last month" tone="accent" />
        <AdminStatCard label="Revenue (MTD)" value={`₹${latest.revenue.toLocaleString("en-IN")}`} icon="ri-money-rupee-circle-line" change={Number(revenueGrowth.toFixed(1))} hint="MoM" tone="secondary" />
      </div>

      <div className="mt-6 rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="font-heading text-base font-bold text-foreground-950">Traffic &amp; listings</h2>
            <p className="mt-0.5 text-xs text-foreground-500">Monthly views against live listings</p>
          </div>
          <div className="flex items-center gap-4 text-xs">
            <span className="inline-flex items-center gap-1.5 text-foreground-600">
              <span className="inline-block h-2.5 w-2.5 rounded-full bg-primary-500"></span> Views
            </span>
            <span className="inline-flex items-center gap-1.5 text-foreground-600">
              <span className="inline-block h-2.5 w-2.5 rounded-full bg-accent-500"></span> Listings
            </span>
          </div>
        </div>
        <div className="mt-4">
          <AdminTrafficChart data={trend} />
        </div>
      </div>

      <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-5">
        <div className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
          <h2 className="font-heading text-base font-bold text-foreground-950">Listings by category</h2>
          <p className="mt-0.5 text-xs text-foreground-500">Top eight categories by count</p>
          <div className="mt-4">
            <AdminCategoryChart data={categoryStats} />
          </div>
        </div>

        <div className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
          <h2 className="font-heading text-base font-bold text-foreground-950">Monthly revenue</h2>
          <p className="mt-0.5 text-xs text-foreground-500">Purchases of plans, features and ads</p>
          <div className="mt-4 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-foreground-500">
                  <th className="pb-2 font-semibold">Month</th>
                  <th className="pb-2 font-semibold">Views</th>
                  <th className="pb-2 font-semibold">Listings</th>
                  <th className="pb-2 font-semibold text-right">Revenue</th>
                </tr>
              </thead>
              <tbody>
                {[...trend].reverse().map((p) => (
                  <tr key={p.label} className="border-t border-background-100">
                    <td className="py-2.5 font-semibold text-foreground-900">{p.label}</td>
                    <td className="py-2.5 text-foreground-700">{p.views.toLocaleString()}</td>
                    <td className="py-2.5 text-foreground-700">{p.listings.toLocaleString()}</td>
                    <td className="py-2.5 text-right font-semibold text-foreground-900">₹{p.revenue.toLocaleString("en-IN")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="mt-6 rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
        <h2 className="font-heading text-base font-bold text-foreground-950">Category distribution</h2>
        <div className="mt-4 space-y-3">
          {categoryStats.map((c) => (
            <div key={c.name}>
              <div className="flex items-center justify-between text-sm">
                <span className="font-medium text-foreground-800">{c.name}</span>
                <span className="font-semibold text-foreground-900">{c.count}</span>
              </div>
              <div className="mt-1.5 h-2 w-full overflow-hidden rounded-full bg-background-200">
                <div
                  className="h-full rounded-full bg-primary-500"
                  style={{ width: `${(c.count / maxCategory) * 100}%` }}
                ></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </>
  );
}